import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/api';
import { mapTeacherId } from '../utils/teacherIdMapper';
import PaymentForm from '../components/PaymentForm';
import { 
  FaCalendarAlt, 
  FaClock, 
  FaDollarSign, 
  FaUser, 
  FaMapMarkerAlt, 
  FaStar, 
  FaArrowLeft,
  FaCreditCard,
  FaPaypal,
  FaCheckCircle,
  FaTimes,
  FaInfoCircle
} from 'react-icons/fa';

interface Teacher {
  _id: string;
  basicInfo: {
    fullName: string;
    country: string;
    profilePicture?: string;
  };
  rates: {
    hourlyRate: number;
    currency: string;
    offersFreeTrial: boolean;
  };
  stats: {
    averageRating: number;
    totalReviews: number;
  };
  profile: {
    bio: string;
  };
  teachingOffer: {
    targetStudents: string[];
  };
}

interface BookingForm {
  scheduledDate: string;
  scheduledTime: string;
  duration: number;
  sessionType: 'trial' | 'regular';
  notes: string;
}

const BookSession: React.FC = () => {
  const { teacherId } = useParams<{ teacherId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [teacher, setTeacher] = useState<Teacher | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bookingForm, setBookingForm] = useState<BookingForm>({
    scheduledDate: '',
    scheduledTime: '',
    duration: 60,
    sessionType: 'regular',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchTeacher();
  }, [teacherId, user, navigate]);

  const fetchTeacher = async () => {
    try {
      setLoading(true);
      const actualTeacherId = mapTeacherId(teacherId || '');
      const response = await fetch(`${API_BASE_URL}/api/teachers/${actualTeacherId}`);
      if (response.ok) {
        const data = await response.json();
        setTeacher(data.data || data);
      } else {
        setError('Teacher not found');
      }
    } catch (error) {
      console.error('Error fetching teacher:', error);
      setError('Failed to load teacher information');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof BookingForm, value: string | number) => {
    setBookingForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const calculateTotal = () => {
    if (!teacher) return 0;
    const rate = bookingForm.sessionType === 'trial' && teacher.rates.offersFreeTrial 
      ? 0 
      : teacher.rates.hourlyRate;
    return (rate * bookingForm.duration) / 60;
  };

  const validateForm = () => {
    if (!bookingForm.scheduledDate) {
      setError('Please select a date');
      return false;
    }
    if (!bookingForm.scheduledTime) {
      setError('Please select a time');
      return false;
    }
    if (bookingForm.duration < 15) {
      setError('Session duration must be at least 15 minutes');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const scheduledDateTime = new Date(`${bookingForm.scheduledDate}T${bookingForm.scheduledTime}`);
      
      const bookingData = {
        scheduledDate: scheduledDateTime.toISOString(),
        duration: bookingForm.duration,
        sessionType: bookingForm.sessionType,
        notes: bookingForm.notes
      };

      const response = await fetch(`${API_BASE_URL}/api/teachers/${mapTeacherId(teacherId || '')}/book`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(bookingData)
      });

      const data = await response.json();

      if (response.ok) {
        setSessionId(data.session._id);
        // If payment is required, show payment form
        if (calculateTotal() > 0) {
          setShowPayment(true);
        } else {
          // Free trial - redirect to success
          navigate(`/session-success/${data.session._id}`);
        }
      } else {
        setError(data.error || 'Failed to book session');
      }
    } catch (error) {
      console.error('Error booking session:', error);
      setError('Failed to book session. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentSuccess = (paymentData: any) => {
    // Update session with payment information
    if (sessionId) {
      // This would typically update the session with payment confirmation
      navigate(`/session-success/${sessionId}`);
    }
  };

  const handlePaymentError = (error: string) => {
    setError(error);
    setShowPayment(false);
  };

  const handlePaymentCancel = () => {
    setShowPayment(false);
  };

  const getAvailableTimes = () => {
    const times = [];
    for (let hour = 9; hour <= 20; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        times.push(time);
      }
    }
    return times;
  };

  const getMinDate = () => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading teacher information...</p>
        </div>
      </div>
    );
  }

  if (error && !teacher) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">{error}</h3>
          <button
            onClick={() => navigate('/teachers')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Teachers
          </button>
        </div>
      </div>
    );
  }

  if (!teacher) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(`/teacher/${teacherId}`)}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <FaArrowLeft className="mr-2" />
              Back to Teacher Profile
            </button>
            <h1 className="text-xl font-semibold text-gray-900">Book Session</h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Booking Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-sm border p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Book Your Session</h2>

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
                  {error}
                </div>
              )}

              {/* Teacher Info */}
              <div className="flex items-center mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-xl font-bold mr-4">
                  {teacher.basicInfo.profilePicture ? (
                    <img
                      src={teacher.basicInfo.profilePicture}
                      alt={teacher.basicInfo.fullName}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                  ) : (
                    teacher.basicInfo.fullName.charAt(0)
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {teacher.basicInfo.fullName}
                  </h3>
                  <div className="flex items-center text-sm text-gray-600">
                    <FaMapMarkerAlt className="mr-1" />
                    {teacher.basicInfo.country}
                    <span className="mx-2">•</span>
                    <FaStar className="text-yellow-400 mr-1" />
                    {teacher.stats.averageRating.toFixed(1)} ({teacher.stats.totalReviews} reviews)
                  </div>
                </div>
              </div>

              {/* Date and Time Selection */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FaCalendarAlt className="inline mr-2" />
                    Date
                  </label>
                  <input
                    type="date"
                    value={bookingForm.scheduledDate}
                    onChange={(e) => handleInputChange('scheduledDate', e.target.value)}
                    min={getMinDate()}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FaClock className="inline mr-2" />
                    Time
                  </label>
                  <select
                    value={bookingForm.scheduledTime}
                    onChange={(e) => handleInputChange('scheduledTime', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select a time</option>
                    {getAvailableTimes().map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Duration Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FaClock className="inline mr-2" />
                  Duration
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {[30, 60, 90].map(duration => (
                    <button
                      key={duration}
                      onClick={() => handleInputChange('duration', duration)}
                      className={`px-4 py-2 border rounded-lg text-center transition-colors ${
                        bookingForm.duration === duration
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      {duration} min
                    </button>
                  ))}
                </div>
              </div>

              {/* Session Type */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Session Type
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => handleInputChange('sessionType', 'regular')}
                    className={`px-4 py-2 border rounded-lg text-center transition-colors ${
                      bookingForm.sessionType === 'regular'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    Regular Session
                  </button>
                  <button
                    onClick={() => handleInputChange('sessionType', 'trial')}
                    className={`px-4 py-2 border rounded-lg text-center transition-colors ${
                      bookingForm.sessionType === 'trial'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {teacher.rates.offersFreeTrial ? 'Free Trial' : 'Trial Session'}
                  </button>
                </div>
              </div>

              {/* Notes */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Notes (Optional)
                </label>
                <textarea
                  value={bookingForm.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Any specific topics you'd like to cover or questions you have..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Submit Button */}
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Booking...' : 'Continue to Payment'}
              </button>
            </div>
          </div>

          {/* Sidebar - Pricing and Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm border p-6 sticky top-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Session Summary</h3>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between">
                  <span className="text-gray-600">Duration</span>
                  <span className="font-medium">{bookingForm.duration} minutes</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Session Type</span>
                  <span className="font-medium capitalize">{bookingForm.sessionType}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Rate</span>
                  <span className="font-medium">
                    ${teacher.rates.hourlyRate}/hour
                  </span>
                </div>
                <hr />
                <div className="flex justify-between text-lg font-semibold">
                  <span>Total</span>
                  <span className="text-blue-600">
                    ${calculateTotal().toFixed(2)}
                  </span>
                </div>
              </div>

              {teacher.rates.offersFreeTrial && bookingForm.sessionType === 'trial' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center">
                    <FaCheckCircle className="text-green-500 mr-2" />
                    <span className="text-green-800 font-medium">Free trial session!</span>
                  </div>
                </div>
              )}

              <div className="text-sm text-gray-600">
                <div className="flex items-start mb-2">
                  <FaInfoCircle className="text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>You'll receive a confirmation email with meeting details after booking.</span>
                </div>
                <div className="flex items-start">
                  <FaInfoCircle className="text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Sessions can be cancelled up to 24 hours before the scheduled time.</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Modal */}
        {showPayment && sessionId && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <PaymentForm
              amount={calculateTotal()}
              currency={teacher.rates.currency}
              sessionId={sessionId}
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentError}
              onCancel={handlePaymentCancel}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default BookSession;
